import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CartCharges, CartItem, CartItemType, CartSectionType } from '../../../../../../models/order/workflow/order-cart.model';
import { ProductType } from '../../../../../../models/order/order.model';
import { InternetProductResponseService } from '../../order-api-response/internet-pd-response.service';
import { InternetCartData } from '../../../../../../models/order/workflow/internet/internet-cart.model';
import { FlattenCustomization } from '../../../../../../models/order/api-models/order-pd-response.model';
import { AdditionalInternetOffersStateService } from '../internet/additional-internet-offers/additional-internet-offers.state.service';

@Injectable({ providedIn: 'root' })
export class CharterInternetCartStateService {
    private internetPdResponseService = inject(InternetProductResponseService);
    private additionalOffersStateService = inject(AdditionalInternetOffersStateService);

    private _cart = signal<InternetCartData | null>(null);
    cart = this._cart.asReadonly();

    constructor() {
        effect(() => {
            const pdResponse = this.internetPdResponseService.productResponse();
            const offers = this.additionalOffersStateService.offers();
            if (pdResponse && offers?.length) {
                this.setupCartFromOrder();
            }
        });
    }

    private getIgnoredCharterCustomizations(): FlattenCustomization[] {
        return this.internetPdResponseService.getCustomizations().filter(cust =>
            cust.customization.DetailedGroupName === 'IgnoreCharterInternetOffers'
        );
    }

    private getSelectedCustomizations(): FlattenCustomization[] {
        const offers = this.additionalOffersStateService.offers();
        const result: FlattenCustomization[] = [];
        for (const offer of offers) {
            const cust = this.internetPdResponseService
                .findCustomizationByChoiceCode(offer.choiceCode);
            if (cust && cust.customization.DetailedGroupName !== 'IgnoreCharterInternetOffers') {
                result.push(cust);
            }
        }
        return result;
    }

    private setupCartFromOrder() {
        const internetCharges = this.setupInternetCharges();
        if (internetCharges) {
            internetCharges.firstBillCharges = this.setupFirstBillCharges();
            internetCharges.todaysCharges = this.setupTodaysCharges();
        }
        this._cart.set(internetCharges);
    }

    private setupFirstBillCharges(): CartCharges {
        const firstBillCharges: CartCharges = {
            totalAmount: 0,
            items: [],
            totalTaxAmount: '0'
        };

        let oneTimeTaxTotal = 0;

        const firstBillCustomizations = this.internetPdResponseService.getCustomizations().filter(cust =>
            cust.customization.CartCategory?.toLocaleLowerCase() === 'firstbill'
            && cust.customization.DetailedGroupName === 'IgnoreCharterInternetOffers'
        );

        for (const cust of firstBillCustomizations) {
            const choices = cust.customization.Choices?.Choice?.filter(
                c => c.CartVisible === 'true'
            ) ?? [];

            for (const choice of choices) {
                const cartItem: CartItem = {
                    disclosure: choice.CartDisclosure || '',
                    amount: choice.Fee?.Fee || 0,
                    name: choice.CartName || choice.Name || '',
                    choiceCode: choice.ChoiceCode || '',
                    choice,
                    customization: cust.customization,
                    metadata: null as any,
                    type: CartItemType.Fee,
                    cartSectionType: CartSectionType.FirstBill,
                    productType: ProductType.Internet
                };
                firstBillCharges.items.push(cartItem);
                firstBillCharges.totalAmount += cartItem.amount;
            }
        }

        for (const cust of firstBillCustomizations) {
            const choice = cust.customization.Choices?.Choice
                ?.find(c => c.TotalOneTimeTaxes && c.TotalOneTimeTaxes !== '');
            if (!choice) continue;
            const oneTimeTax = parseFloat(choice.TotalOneTimeTaxes || '0');
            if (!isNaN(oneTimeTax) && oneTimeTax > 0) {
                oneTimeTaxTotal += oneTimeTax;
            }
        }

        firstBillCharges.totalTaxAmount = oneTimeTaxTotal.toFixed(2);
        firstBillCharges.totalAmount += oneTimeTaxTotal;
        return firstBillCharges;
    }

    private setupInternetCharges(): InternetCartData {
        const internetComponent = this.internetPdResponseService.getProductComponent();
        const internetCartData: InternetCartData = {
            name: internetComponent?.ComponentName || '',
            price: internetComponent?.Price || 0,
            promoPrice: internetComponent?.PromoPrice || 0,
            monthly: {
                totalAmount: internetComponent?.Price || 0,
                totalTaxAmount: '0',
                items: [],
                promotions: { totalAmount: 0, items: [] }
            }
        };

        let monthlyTaxTotal = 0;
        const offers = this.additionalOffersStateService.offers();
        const selectedCustomizations = this.getSelectedCustomizations();

        for (const cust of selectedCustomizations) {
            const matchedOffer = offers.find(o =>
                cust.customization.Choices?.Choice?.some(c => c.ChoiceCode === o.choiceCode)
            );
            if (!matchedOffer) continue;

            const choice = cust.customization.Choices?.Choice
                ?.find(c => c.ChoiceCode === matchedOffer.choiceCode);
            if (!choice || choice.Fee?.FeeTerm !== 'Monthly') continue;

            const monthlyTax = parseFloat(choice.TotalMonthlyTaxes || '0');
            if (!isNaN(monthlyTax)) monthlyTaxTotal += monthlyTax;

            const cartItem: CartItem = {
                disclosure: choice.CartDisclosure || '',
                amount: choice.Fee?.Fee || 0,
                name: choice.CartName || choice.Name || '',
                choiceCode: choice.ChoiceCode || '',
                choice,
                customization: cust.customization,
                metadata: null as any,
                type: CartItemType.Fee,
                cartSectionType: CartSectionType.Monthly,
                productType: ProductType.Internet
            };

            if (cartItem.amount < 0) {
                cartItem.type = CartItemType.Promotion;
                internetCartData.monthly.promotions.items.push(cartItem);
                internetCartData.monthly.promotions.totalAmount += cartItem.amount;
            } else {
                internetCartData.monthly.items.push(cartItem);
                internetCartData.monthly.totalAmount += cartItem.amount;
            }
        }

        const ignoredMonthlyCustomizations = this.getIgnoredCharterCustomizations().filter(cust =>
            cust.customization.CartCategory === 'Internet'
        );

        for (const cust of ignoredMonthlyCustomizations) {
            const choice = cust.customization.Choices?.Choice
                ?.find(c => c.TotalMonthlyTaxes && c.TotalMonthlyTaxes !== '');
            if (!choice) continue;
            const monthlyTax = parseFloat(choice.TotalMonthlyTaxes || '0');
            if (!isNaN(monthlyTax) && monthlyTax > 0) {
                monthlyTaxTotal += monthlyTax;
            }
        }

        internetCartData.monthly.totalTaxAmount = monthlyTaxTotal.toFixed(2);
        internetCartData.monthly.totalAmount += internetCartData.monthly.promotions.totalAmount;
        internetCartData.monthly.totalAmount += monthlyTaxTotal;
        return internetCartData;
    }

    private setupTodaysCharges(): CartCharges {
        return { totalAmount: 0, items: [] };
    }

    isCartEmpty = computed(() => !this._cart());
}